import { requireService } from '@profgeosoft/di-ez';
import { AddRoundLight, ChooseRow, CloseRoundLight, DoneLight, EditLightBig, SettingLineLight } from '@repo/go-icons';
import { Button, Tooltip } from '@repo/go-ui';
import { clsx } from 'clsx';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TableHeaderOrientationToggle } from 'src/components/directory-table/components/table-header-orientation';
import { DEFAULT_TABLE_HEADER_ORIENTATION } from 'src/components/directory-table/header-orientation';
import { ImportButton } from 'src/features/import-modal';
import { wrap } from 'src/packages/mobx-di/wrap';

import type { MainDirectoryService } from 'src/services/directory-service/main-directory-service';

import styles from '../directory-page-header.module.scss';

type Props = {
  directory: MainDirectoryService;
  onTableSettingsClick(): void;
};

export const EditModeControls = wrap<Props>(function EditModeControls({ directory, onTableSettingsClick }) {
  const directoryPageHeader = requireService('directoryPageHeader');
  const directoryPage = requireService('directoryPage');
  const directoryListService = requireService('directoriesListService');
  const userService = requireService('userService');
  const { t } = useTranslation('appbar');

  const discardChanges = () => {
    if (!directoryPage.hasChanges) {
      directory.discardChanges();
      directoryPage.setIsEditMode(false);
      directoryPage.setIsRowSelectionMode(false);
      directoryPage.removeSavedEditModeData();
      return;
    }

    directoryPageHeader.onEditModeOffByDiscard(() => {
      directory.discardChanges();
      directoryPage.removeSavedEditModeData();
    });
  };

  const isEditingAvailable = useMemo(() => {
    const dir = directoryPage.directoryModelService?.view?.objectType ?? directory.directoryName;

    const objType = directoryListService.getObjectType(dir);

    return objType?.isEditingAvailable !== false;
  }, [directory.directoryName, directoryListService, directoryPage.directoryModelService?.view?.objectType]);

  useEffect(() => {
    directoryListService.setIsDirectoryChangingBlocked(directoryPage.isEditing);
  }, [directoryListService, directoryPage.isEditing]);

  const displayManager = directory.tableSettingsManager;
  const headerOrientationToggle = (() => {
    if (!displayManager) {
      return null;
    }

    return (
      <TableHeaderOrientationToggle
        className={styles.iconWrapper}
        orientation={displayManager.headerOrientation ?? DEFAULT_TABLE_HEADER_ORIENTATION}
        onToggle={displayManager.toggleHeaderOrientation}
      />
    );
  })();

  if (!directoryPage.isEditing) {
    return (
      <>
        {isEditingAvailable && (
          <Button
            variant="flat"
            className={styles.iconWrapper}
            onClick={() => {
              directoryPage.setIsEditMode(true);
              directoryPage.setIsRowSelectionMode(true);
            }}
          >
            <EditLightBig />
          </Button>
        )}
        <Button variant="flat" className={styles.iconWrapper} onClick={onTableSettingsClick}>
          <SettingLineLight />
        </Button>
        <Button
          className={clsx(styles.chooseRowButton, directoryPage.isRowSelectionModeOn && styles.pressed)}
          onClick={() => {
            directoryPage.setIsRowSelectionMode(!directoryPage.isRowSelectionModeOn);
          }}
        >
          <ChooseRow />
        </Button>
        {headerOrientationToggle}
      </>
    );
  }

  return (
    <div className={styles.editModeWrapper}>
      {!directoryPage.currentDirectory?.view.rowsCreationIsBlocked && (
        <Button onClick={directoryPage.addNewRecord} icon={<AddRoundLight />}>
          {t('appbar.addButton')}
        </Button>
      )}
      <Button variant="danger" className={styles.quaterButton} onClick={discardChanges}>
        <CloseRoundLight />
      </Button>
      <Button
        variant="success"
        disabled={!directoryPage.hasChanges}
        className={styles.quaterButton}
        onClick={directoryPage.saveChanges}
      >
        <DoneLight />
      </Button>
      {/* Временно отключено */}
      {/* <div className={styles.iconWrapper}>
          <DetailVertCustom />
        </div> */}
      <Tooltip title={t('tooltips.notAvialable')}>
        <Button variant="flat" className={clsx(styles.iconWrapper, styles.disabled)} disabled={directoryPage.isEditing}>
          <SettingLineLight />
        </Button>
      </Tooltip>
      {directory.isImportAvailable && <ImportButton directory={directory} className={styles.iconWrapper} />}
      {headerOrientationToggle}
    </div>
  );
});
